import { useCallback, useEffect, useRef, useState } from 'react'

interface AsyncState<T> {
  data: T | null
  loading: boolean
  error: string | null
  reload: () => void
}

export function useAsync<T>(fn: () => Promise<T>, deps: unknown[] = []): AsyncState<T> {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const mountedRef = useRef(true)
  // Monotonic token identifying the most recent load(). Each call captures its own
  // token; only the latest may write state. This guards against overlapping in-flight
  // requests (e.g. a mount fetch racing a fetch triggered by a slicer set in the same
  // tick) — without it, whichever response lands last wins, so a stale earlier request
  // can clobber the correct later one. mountedRef alone can't catch this: it's shared,
  // so a re-run resets it to true and lets the stale response through.
  const callIdRef = useRef(0)

  const load = useCallback(() => {
    const callId = ++callIdRef.current
    setLoading(true)
    setError(null)
    fn()
      .then((d) => { if (mountedRef.current && callId === callIdRef.current) { setData(d); setLoading(false) } })
      .catch((e) => { if (mountedRef.current && callId === callIdRef.current) { setError(e.message || 'Request failed'); setLoading(false) } })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  useEffect(() => {
    mountedRef.current = true
    load()
    return () => { mountedRef.current = false }
  }, [load])

  return { data, loading, error, reload: load }
}
